// Live's Effects tab (#265, spec docs/specs/editor/effects.md): its 102 tiles in its rows and
// order, by live's keys and the names its tiles show. A tile is one of live's `pixi-filters`
// filters, one of its built-in shaders, or one of its 32 custom shaders (the CUSTOM badge). The
// editor draws every one: a tile's kind is the contract's `EffectKind` of its key (#265 part 3).
// The descriptions are ours, of what each looks like, for the search (live searches its own).
import { EffectKind } from "../../api/generated/model";

export type EffectCategoryId = "camera" | "light" | "glitch" | "retro" | "distort" | "sparkles" | "color";

/** Live's rows, in its order. */
export const EFFECT_CATEGORIES: readonly { id: EffectCategoryId; label: string }[] = [
  { id: "camera", label: "Camera & Motion" },
  { id: "light", label: "Light & Glow" },
  { id: "glitch", label: "Glitch & Cyber" },
  { id: "retro", label: "Retro & Film" },
  { id: "distort", label: "Distort & Warp" },
  { id: "sparkles", label: "Sparkles & FX" },
  { id: "color", label: "Color & Mood" },
];

/** How live draws an effect: a `pixi-filters` filter, a built-in shader, or a custom one. */
export type EffectSource = "pixi" | "shader" | "custom";

export interface EffectInfo {
  /** Live's key, as live searches it. */
  key: string;
  /** The contract's slug of the key (`fadeIn` is `fade-in`). */
  slug: string;
  name: string;
  category: EffectCategoryId;
  source: EffectSource;
  description: string;
  /** The kind the editor draws. */
  kind: EffectKind;
}

// Live's key, the tile's name, how live draws it, and what it looks like (ours, for the search).
type Row = readonly [key: string, name: string, source: EffectSource, description: string];

const ROWS: Record<EffectCategoryId, readonly Row[]> = {
  camera: [
    ["rotationMovement", "Rotation Movement", "shader", "a continuous camera roll"],
    ["spring", "Spring", "shader", "a bouncy elastic zoom"],
    ["warpTransition", "Warp Transition", "shader", ""],
    ["pixelateTransition", "Pixelate Transition", "shader", ""],
    ["focusTransition", "Focus Transition", "shader", ""],
    ["scaleMoveBlur", "Scale Move Blur", "shader", "a pulsing scale with motion blur"],
    ["cameraMove", "Camera Move", "shader", "a handheld pan and tilt"],
    ["fastZoom", "Fast Zoom", "shader", "rapid zoom punches in and out"],
    ["fadeIn", "Fade In", "shader", ""],
    ["fadeOut", "Fade Out", "shader", ""],
    ["cameraShake", "Camera Shake", "custom", "a high-energy shake with motion blur"],
    ["radialZoomRush", "Zoom Rush", "custom", "a pulsing radial zoom into the centre"],
    ["zoomRotate", "Zoom Rotate", "custom", "a rotating roll with a zoom swing"],
    ["dutchTiltRecoil", "Dutch Tilt Recoil", "custom", "a swinging Dutch angle with recoil"],
    ["whipSnapPunch", "Whip Snap Punch", "custom", "a punch-in zoom with motion streaks"],
    ["earthquakeTremor", "Earthquake Tremor", "custom", "a rumbling shake with colour fringes"],
  ],
  light: [
    ["shine", "Shine Flare", "shader", "a shimmering light sweep over the highlights"],
    ["blink", "Blink", "shader", "blinking black bars"],
    ["flashLoop", "Flash Loop", "shader", "continuous exposure flash pulses"],
    ["neonFlash", "Neon Flash", "shader", "multicoloured neon pulses"],
    ["laser", "Laser Beams", "shader", "sweeping laser streaks"],
    ["sparks", "Sparks", "shader", "golden spark particles"],
    ["blackFlash", "Black Flash", "shader", "rhythmic black exposure cuts"],
    ["brightPulse", "Bright Pulse", "shader", "a pulsing luminance bloom"],
    ["strobe", "Strobe Flash", "custom", "a fast white strobe"],
    ["edgeGlow", "Neon Edge Glow", "custom", "edges glowing in neon colours"],
    ["anamorphicStreak", "Anamorphic Flare Streak", "custom", "horizontal icy blue flare streaks"],
    ["lensGlintRainbow", "Prismatic Glint", "custom", "star glints with rainbow dispersion"],
    ["solarizeFlare", "Solarize Sunburst", "custom", "a warm golden sunburst with rays"],
  ],
  glitch: [
    ["glitch", "Digital Glitch", "shader", "horizontal slices displaced"],
    ["rgbGlitch", "RGB Glitch", "shader", "the colour channels tearing"],
    ["rgbShift", "RGB Shift", "shader", "subtle colour fringes"],
    ["slitScan", "Slit Scan", "shader", "a slit-scan strip distortion"],
    ["slitScanGlitch", "Slit Scan Glitch", "shader", "warped slit-scan tearing"],
    ["tvScanlines", "TV Scanlines", "shader", "a CRT scanline raster"],
    ["rgbSplitPulse", "RGB Split Pulse", "custom", "a pulsing chromatic split"],
    ["vhsTapeFX", "VHS Tape FX", "custom", "VHS tracking bars and noise"],
    ["datamoshMelt", "Datamosh Smear", "custom", "datamosh pixel smearing"],
    ["badAntennaStatic", "Bad CRT Antenna", "custom", "rolling tear bars and static"],
    ["pixelSorting", "Pixel Sort Waterfall", "custom", "bright pixels streaking down"],
  ],
  retro: [
    ["sepia", "Sepia", "shader", "a warm sepia monochrome"],
    ["halftone", "Halftone Dots", "shader", "comic halftone dots"],
    ["grayscale", "Grayscale", "shader", "black and white"],
    ["filmStripPro", "Film Strip Pro", "shader", "sprocket holes on a rolling film frame"],
    ["paperBreakReveal", "Paper Break", "shader", "torn paper edges framing the video"],
    ["filmGrain", "35mm Film Grain", "custom", "moving film grain"],
    ["kodachrome8mm", "8mm Kodachrome", "custom", "a 1960s home movie with gate weave and grain"],
    ["technicolorTwoStrip", "Technicolor 2-Strip", "custom", "a 1930s red and cyan palette"],
    ["agedNewspaper", "Aged Newsprint", "custom", "yellowed newsprint with ink bleed"],
  ],
  distort: [
    ["sinewave", "Sinewave", "shader", "a sine wave distortion"],
    ["swirlMovement", "Swirl Movement", "shader", "a vortex swirl at the centre"],
    ["distort", "Distort", "shader", "an organic ripple displacement"],
    ["perspectiveSingle", "Perspective Tilt", "shader", "a 3D plane tilt"],
    ["distortSpin", "Distort Spin", "shader", "a spinning liquid vortex"],
    ["distortGrid", "Distort Grid", "shader", "grid tiles displaced"],
    ["mirrorTile", "Mirror Tile", "shader", "mirrored quadrants"],
    ["curtainOpen", "Curtain Open", "shader", "a stage curtain opening"],
    ["curtainBlur", "Curtain Blur", "shader", "parting blur bands"],
    ["distortV2", "Distort V2", "shader", "water surface ripples"],
    ["waveDistort", "Wave Distort", "shader", "horizontal sine waves"],
    ["wave", "Wave", "shader", "gentle liquid waves"],
    ["heatwave", "Heatwave", "custom", "a rising heat shimmer"],
    ["fisheyeBarrel", "Ultra Fisheye", "custom", "an extreme barrel lens with fringed edges"],
    ["underwaterRefraction", "Underwater Caustic", "custom", "water ripples with caustic light"],
    ["blackHoleVortex", "Singularity Warp", "custom", "space bending into a dark centre"],
  ],
  sparkles: [
    ["heartSparkles", "Heart Sparkles", "custom", "glowing hearts drifting up"],
    ["goldenGlitter", "Golden Glitter Dust", "custom", "golden glitter with star pings"],
    ["bubbleSparkles", "Bubble Sparkles", "custom", "floating bubbles with rainbow highlights"],
    ["fairyDust", "Fairy Dust Swirl", "custom", "a spiral of pastel fairy dust"],
  ],
  color: [
    ["adjustmentFilter", "Adjustment Filter", "pixi", ""],
    ["advancedBloomFilter", "Advanced Bloom Filter", "pixi", ""],
    ["backdropBlurFilter", "Backdrop Blur Filter", "pixi", ""],
    ["bloomFilter", "Bloom Filter", "pixi", ""],
    ["colorMapFilter", "Color Map Filter", "pixi", ""],
    ["convolutionFilter", "Convolution Filter", "pixi", ""],
    ["dotFilter", "Dot Filter", "pixi", ""],
    ["glitchFilter", "Glitch Filter", "pixi", ""],
    ["glowFilter", "Glow Filter", "pixi", ""],
    ["godrayFilter", "Godray Filter", "pixi", ""],
    ["grayscaleFilter", "Grayscale Filter", "pixi", ""],
    ["hslAdjustmentFilter", "Hsl Adjustment Filter", "pixi", ""],
    ["kawaseBlurFilter", "Kawase Blur Filter", "pixi", ""],
    ["motionBlurFilter", "Motion Blur Filter", "pixi", ""],
    ["oldFilmFilter", "Old Film Filter", "pixi", ""],
    ["radialBlurFilter", "Radial Blur Filter", "pixi", ""],
    ["reflectionFilter", "Reflection Filter", "pixi", ""],
    ["rgbSplitFilter", "Rgb Split Filter", "pixi", ""],
    ["tiltShiftFilter", "Tilt Shift Filter", "pixi", ""],
    ["pixelate", "Pixelate", "shader", "an 8-bit pixel mosaic"],
    ["duotone", "Duotone", "shader", "a two-tone colour map"],
    ["tritone", "Tritone", "shader", "a three-tone grade"],
    ["hueShift", "Hue Shift", "shader", "a cycling hue"],
    ["invert", "Invert Color", "shader", "the negative"],
    ["vignette", "Vignette", "shader", "a darkened border"],
    ["chromatic", "Chromatic Flare", "shader", "a chromatic aberration shift"],
    ["inverseAperture", "Inverse Aperture", "shader", "a round iris vignette"],
    ["hdr", "HDR Boost", "shader", "an HDR contrast boost"],
    ["hdrV2", "HDR Cinematic", "shader", "a cinematic HDR grade"],
    ["chromaticAura", "Chromatic Aura", "custom", "a soft prism glow round the highlights"],
    ["cyberpunkNeon", "Cyberpunk Neon", "custom", "teal shadows and magenta highlights"],
    ["thermalInfrared", "Thermal Heatmap", "custom", "a false-colour thermal camera"],
    ["cinematicTealOrange", "Teal & Orange Film", "custom", "teal shadows and amber highlights"],
  ],
};

/** The contract's slug of live's key: words split at each capital, lower case, by hyphens. */
export function slugOf(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();
}

const KINDS: ReadonlySet<string> = new Set<string>(Object.values(EffectKind));

/** The contract's kind of live's key: every tile's is one (`effects.test.ts`). */
function kindOf(key: string): EffectKind {
  const slug = slugOf(key);
  if (!KINDS.has(slug)) throw new Error(`Live's effect ${key} has no kind in the contract.`);
  return slug as EffectKind;
}

/** Every tile of live's tab, row by row, in its order. */
export const EFFECTS: readonly EffectInfo[] = EFFECT_CATEGORIES.flatMap(({ id }) =>
  ROWS[id].map(([key, name, source, description]) => {
    const slug = slugOf(key);
    return {
      key,
      slug,
      name,
      category: id,
      source,
      description,
      kind: kindOf(key),
    };
  }),
);

/** What a tile dragged to the timeline carries: the kind of its effect. */
export const EFFECT_DRAG_TYPE = "application/x-cai-effect";

/** A drawn kind's name, as live's tile shows it. */
export function effectName(kind: EffectKind): string {
  return EFFECTS.find((each) => each.kind === kind)?.name ?? kind;
}

/** Live's search: the effects whose name, key or description holds the words typed, in any case. */
export function searchEffects(list: readonly EffectInfo[], query: string): readonly EffectInfo[] {
  const words = query.trim().toLowerCase();
  if (words === "") return list;
  return list.filter((each) =>
    [each.name, each.key, each.description].some((field) => field.toLowerCase().includes(words)),
  );
}
